/**
 * Centralized API client with authentication and cache management.
 * All API calls should go through this client to ensure consistent behavior.
 */

const API_BASE = import.meta.env.VITE_PHOTO_API_URL || 'https://where-in-the-world-photos.armincerf.workers.dev';
const ADMIN_API_KEY = import.meta.env.VITE_ADMIN_API_KEY || '';

/**
 * Clear the service worker's cache for specific API patterns.
 */
function clearSwCache(pattern: string): void {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'clearCachePattern', pattern });
    console.log(`Sent cache clear for pattern: ${pattern}`);
  }
}

/**
 * Determine which cache patterns to clear based on the endpoint.
 */
function getCachePatternsForEndpoint(endpoint: string): string[] {
  if (endpoint.includes('/api/photos')) {
    return ['/api/photos'];
  }
  if (endpoint.includes('/api/country-data')) {
    return ['/api/country-data'];
  }
  if (endpoint.includes('/api/stats')) {
    return ['/api/stats'];
  }
  return [];
}

/**
 * Central API fetch function with:
 * - Automatic admin API key injection for write operations
 * - Service worker cache clearing after mutations
 * - Consistent error handling
 */
export async function apiClient<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const method = options?.method || 'GET';
  const isWriteOperation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      // Automatically include admin key for write operations
      ...(isWriteOperation && ADMIN_API_KEY ? { 'X-Admin-Key': ADMIN_API_KEY } : {}),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  // Clear relevant SW caches after successful write operations
  if (isWriteOperation) {
    const patterns = getCachePatternsForEndpoint(endpoint);
    patterns.forEach(clearSwCache);
  }

  return response.json();
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T>(endpoint: string) => apiClient<T>(endpoint),

  post: <T>(endpoint: string, body: unknown) =>
    apiClient<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  put: <T>(endpoint: string, body: unknown) =>
    apiClient<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body: unknown) =>
    apiClient<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string) =>
    apiClient<T>(endpoint, { method: 'DELETE' }),
};
